import { useGetApiCsvSchemasPlantPlantId } from '@/api/gen/api';
import HierarchicalFilter from '@/components/HierarchicalFilter';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Visibility,
  Storage,
} from '@mui/icons-material';
import { CsvSchemaResponse } from '@/generated/data-contracts';
import { CreateButton } from 'react-admin';

export default function CsvSchemasList() {
  const navigate = useNavigate();
  const [plant, setPlant] = useState<number | undefined>();
  const [expandedSchemas, setExpandedSchemas] = useState<Set<number>>(
    new Set()
  );

  const handleVariableFilterChange = (selections: { plantId?: number }) => {
    setPlant(selections.plantId);
  };

  const { data, error, isLoading } = useGetApiCsvSchemasPlantPlantId(
    plant ?? 0,
    {
      query: {
        enabled: !!plant,
      },
    }
  );

  const handleToggleSchema = (schemaId: number) => {
    setExpandedSchemas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(schemaId)) {
        newSet.delete(schemaId);
      } else {
        newSet.add(schemaId);
      }
      return newSet;
    });
  };

  const handleViewSchema = (schemaId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/CsvSchemas/${schemaId}/show`);
  };

  const handleViewSchemaData = (schemaId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/CsvSchemas/${schemaId}/data`);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDataTypeColor = (dataType?: string) => {
    switch (dataType) {
      case 'String':
        return 'primary';
      case 'Int':
        return 'success';
      case 'Double':
        return 'info';
      case 'Bool':
        return 'warning';
      case 'DateTime':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant='h4' gutterBottom sx={{ mb: 0 }}>
          Esquemas CSV
        </Typography>

        <CreateButton
          label='Añadir'
          variant='contained'
          sx={{
            fontSize: '1rem',
            fontWeight: 'bold',
            boxShadow: 3,
            '&:hover': {
              boxShadow: 6,
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease',
          }}
        />
      </Box>
      <Box sx={{ mb: 3 }}>
        <HierarchicalFilter
          levels={['plant']}
          onChange={handleVariableFilterChange}
          size='small'
        />
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity='error' sx={{ mt: 2 }}>
          Error al cargar los esquemas CSV
        </Alert>
      )}

      {!plant && !isLoading && (
        <Alert severity='info' sx={{ mt: 2 }}>
          Seleccione una planta para ver los esquemas CSV
        </Alert>
      )}

      {data && data && data.length > 0 && (
        <Paper elevation={2}>
          <List>
            {data.map((schema: CsvSchemaResponse) => (
              <React.Fragment key={schema.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleToggleSchema(schema.id!)}
                  >
                    <ListItemText
                      primary={
                        <Typography variant='h6'>{schema.name}</Typography>
                      }
                      secondary={
                        <Typography variant='body2' color='text.secondary'>
                          Creado: {formatDate(schema.createdUtc)} | Columnas:{' '}
                          {schema.columns?.length || 0}
                        </Typography>
                      }
                    />
                    <IconButton
                      edge='end'
                      aria-label='ver detalles'
                      onClick={e => handleViewSchema(schema.id!, e)}
                      sx={{ mr: 0.5 }}
                      color='primary'
                    >
                      <Visibility />
                    </IconButton>
                    <IconButton
                      edge='end'
                      aria-label='ver datos'
                      onClick={e => handleViewSchemaData(schema.id!, e)}
                      sx={{ mr: 1 }}
                      color='secondary'
                    >
                      <Storage />
                    </IconButton>
                    {expandedSchemas.has(schema.id!) ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </ListItemButton>
                </ListItem>

                <Collapse
                  in={expandedSchemas.has(schema.id!)}
                  timeout='auto'
                  unmountOnExit
                >
                  <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant='subtitle1' gutterBottom>
                      Columnas del esquema
                    </Typography>
                    <TableContainer>
                      <Table size='small'>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <strong>Encabezado</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Tipo de Dato</strong>
                            </TableCell>
                            <TableCell align='center'>
                              <strong>Requerido</strong>
                            </TableCell>
                            <TableCell align='center'>
                              <strong>Clave Primaria</strong>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {schema.columns?.map(column => (
                            <TableRow key={column.id}>
                              <TableCell>{column.header}</TableCell>
                              <TableCell>
                                <Chip
                                  label={column.dataType}
                                  color={getDataTypeColor(column.dataType)}
                                  size='small'
                                />
                              </TableCell>
                              <TableCell align='center'>
                                {column.isRequired ? (
                                  <Chip label='Sí' color='error' size='small' />
                                ) : (
                                  <Chip
                                    label='No'
                                    variant='outlined'
                                    size='small'
                                  />
                                )}
                              </TableCell>
                              <TableCell align='center'>
                                {column.isPrimary ? (
                                  <Chip
                                    label='Sí'
                                    color='primary'
                                    size='small'
                                  />
                                ) : (
                                  <Chip
                                    label='No'
                                    variant='outlined'
                                    size='small'
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {data && data && data.length === 0 && plant && (
        <Alert severity='info' sx={{ mt: 2 }}>
          No hay esquemas CSV disponibles para esta planta
        </Alert>
      )}
    </Box>
  );
}
